'use client';
import { useEffect, useRef } from 'react';
import styles from './styles/Content.module.css';
import BlockRendererClient from '@/app/_components/BlocksRendererCustom';
import YouTubeVideo from './YouTube.client';
import Image from 'next/image';

export default function Content({ data }: { data: any }) {
    // console.dir(data);
    const assetsBaseUrl = process.env.NEXT_PUBLIC_STRAPI_URL_INTERNAL;
    const { BoxName, Description, MOQ, videoUrl, VideoIfYoutube, VideoYoutubeID, Additional } = data;

    const descriptionRef = useRef<HTMLElement | null>(null);
    const videoRef = useRef<HTMLElement | null>(null);
    const infoRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const handleScroll = (targetRef: React.RefObject<HTMLElement>) => {
            const section = targetRef.current;
            if (section) {
                const yOffset = -75; // Offset for sticky headers or other elements
                const y = section.getBoundingClientRect().top + window.scrollY + yOffset;
                window.scrollTo({ top: y, behavior: 'smooth' });
            }
        };

        const titleItems = [
            { elementId: 'description', targetRef: descriptionRef },
            { elementId: 'video', targetRef: videoRef },
            { elementId: 'otherInfo', targetRef: infoRef },
        ];

        titleItems.forEach(({ elementId, targetRef }) => {
            const element = document.getElementById(elementId);
            if (element) {
                element.addEventListener('click', () => handleScroll(targetRef));
            }
        });

        return () => {
            titleItems.forEach(({ elementId, targetRef }) => {
                const element = document.getElementById(elementId);
                if (element) {
                    element.removeEventListener('click', () => handleScroll(targetRef));
                }
            });
        };
    }, []);

    return (
        <section className="mx-auto max-w-[980px] py-16">
            <h1 className={`mb-6`}>{BoxName}</h1>
            <div className={styles.TitleTab}>
                <div id="description" className={styles.TitleItem}>
                    Description
                </div>
                <div id="video" className={styles.TitleItem}>
                    Video
                </div>
                <div id="otherInfo" className={styles.TitleItem}>
                    Other Information
                </div>
            </div>
            <hr className={`mb-4`} />
            <section ref={descriptionRef} id="desc" className="pt-4">
                <BlockRendererClient content={Description as any} />
                <p>
                    <span className={`text-gray-700`}>Our minimum customized quantity:</span>
                    <br />
                    <span className={`font-medium`}>{MOQ}</span>
                </p>
            </section>
            <section ref={videoRef} id="video" className="pt-4">
                {(VideoIfYoutube && VideoYoutubeID) || videoUrl ? (
                    <>
                        <div className={`py-6`}>
                            <p className={`font-bold`}>Video Show:</p>
                            {VideoIfYoutube && VideoYoutubeID ? (
                                <YouTubeVideo videoId={VideoYoutubeID} />
                            ) : (
                                <video width="640" height="360" controls autoPlay muted>
                                    <source src={`${assetsBaseUrl}${videoUrl}`} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            )}
                        </div>
                    </>
                ) : null}
            </section>
            <section ref={infoRef} id="info" className="pt-4">
                <div className={`py-6`}>
                    <p className={`font-bold`}>Other Information:</p>
                    <BlockRendererClient content={Additional as any} />
                </div>
                <div className={styles.IconCardWrapper}>
                    <p className={`font-bold mb-6`}>Sustainability</p>
                    <div className={styles.IconCardList}>
                        <div className={styles.IconCard}>
                            <div className={styles.IconCardImg}>
                                <Image className={`w-auto h-auto`} src={`/images/custom/208.png`} alt={'box'} width={42} height={40} unoptimized />
                            </div>
                            <div>Recycled</div>
                        </div>
                        <div className={styles.IconCard}>
                            <div className={styles.IconCardImg}>
                                <Image className={`w-auto h-auto`} src={`/images/custom/207.png`} alt={'box'} width={37} height={40} unoptimized />
                            </div>
                            <div>Compostable</div>
                        </div>
                        <div className={styles.IconCard}>
                            <div className={styles.IconCardImg}>
                                <Image className={`w-auto h-auto`} src={`/images/custom/206.png`} alt={'box'} width={35} height={35} unoptimized />
                            </div>
                            <div>Reusable</div>
                        </div>
                        <div className={styles.IconCard}>
                            <div className={styles.IconCardImg}>
                                <Image className={`w-auto h-auto`} src={`/images/custom/205.png`} alt={'box'} width={33} height={40} unoptimized />
                            </div>
                            <div>FSC Certified</div>
                        </div>
                    </div>
                </div>
            </section>
        </section>
    );
}
